import { type FC, type ReactNode, useCallback, useMemo, useRef, useState } from "react"
import { useBeforeUnload } from "react-router"
import styled, { useTheme } from "styled-components"
import randomUUID from "uuid-random"

import { startVideoUpload } from "@forento/ui/utilities/video"

import { CheckmarkIcon, CloseIcon } from "~/components/Icon"
import LoadingIndicator from "~/components/LoadingIndicator"
import { useToast } from "~/contexts/ToastContext"
import { componentBackgroundColor, componentBorderColor, dangerColor, successColor } from "~/utilities/styles"
import trpc from "~/utilities/trpc"

import VideoUploadContext, { type VideoUpload, type VideoUploadValue } from "."

const VideoUploadProvider: FC<{ children?: ReactNode }> = ({ children }) => {
	const { primaryColor } = useTheme()
	const toast = useToast()

	const [uploadingVideos, setUploadingVideos] = useState<VideoUpload[]>([])
	// Tracked synchronously so a second drop is rejected even before the first upload reaches state
	const activePageIds = useRef(new Set<number>())

	const handleUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			if (uploadingVideos.length === 0) return

			event.preventDefault()
			const message =
				"Some uploads are still in progress. Reloading or closing the page will cancel the uploads. Are you sure?"
			event.returnValue = message
			return message
		},
		[uploadingVideos],
	)
	useBeforeUnload(handleUnload, { capture: true })

	const value = useMemo<VideoUploadValue>(
		() => ({
			async start(args: { file: File; pageId: number; title: string }) {
				if (activePageIds.current.has(args.pageId)) {
					toast.showError("A video is already uploading to this page.")
					return null
				}
				activePageIds.current.add(args.pageId)

				const id = randomUUID()
				try {
					const { bunnyVideoId } = await trpc.course.createVideoByPageId.mutate(args.pageId)
					const videoUpload = await trpc.video.initializeUpload.query({ bunnyVideoId })
					setUploadingVideos(current => [
						...current,
						{ id, pageId: args.pageId, bunnyVideoId, label: args.title, status: "preparing" },
					])
					await startVideoUpload({
						file: args.file,
						bunnyVideoId,
						libraryId: videoUpload.libraryId,
						signature: videoUpload.signature,
						onProgress: async upload => {
							if (upload.status === "uploading") {
								setUploadingVideos(current => {
									const uploads = [...current]
									const uploadIndex = uploads.findIndex(x => x.id === id)
									uploads[uploadIndex] = {
										...uploads[uploadIndex],
										status: "uploading",
										progress: upload.progress,
									}
									return uploads
								})
							} else if (upload.status === "failed") {
								activePageIds.current.delete(args.pageId)
								setUploadingVideos(current => {
									const uploads = [...current]
									const uploadIndex = uploads.findIndex(x => x.id === id)
									uploads[uploadIndex] = { ...uploads[uploadIndex], status: "failed" }
									return uploads
								})

								setTimeout(() => {
									setUploadingVideos(current => current.filter(x => x.id !== id))
								}, 5000)
							} else if (upload.status === "completed") {
								activePageIds.current.delete(args.pageId)
								// tus calls this after start() returns, so the surrounding try/catch no longer applies
								try {
									await trpc.course.completeVideoByPageId.mutate(args.pageId)
								} catch (error) {
									console.error(error)
									toast.showError("Failed to finish the video upload. Please try again.")
									setUploadingVideos(current =>
										current.map(x => (x.id === id ? { ...x, status: "failed" } : x)),
									)
									setTimeout(() => {
										setUploadingVideos(current => current.filter(x => x.id !== id))
									}, 5000)
									return
								}
								setUploadingVideos(current => {
									const uploads = [...current]
									const uploadIndex = uploads.findIndex(x => x.id === id)
									uploads[uploadIndex] = { ...uploads[uploadIndex], status: "completed" }
									return uploads
								})

								setTimeout(() => {
									setUploadingVideos(current => current.filter(x => x.id !== id))
								}, 5000)
							}
						},
					})
				} catch (error) {
					console.error(error)
					activePageIds.current.delete(args.pageId)
					setUploadingVideos(current => current.filter(x => x.id !== id))
					toast.showError("Failed to start the video upload. Please try again.")
					return null
				}

				return { id }
			},
			getByPageId(pageId) {
				return uploadingVideos.find(x => x.pageId === pageId) ?? null
			},
		}),
		[uploadingVideos, toast],
	)

	return (
		<VideoUploadContext.Provider value={value}>
			{uploadingVideos.length > 0 && (
				<Container>
					{uploadingVideos.map(upload => (
						<Upload key={upload.id} $status={upload.status}>
							{upload.status === "preparing" ? (
								<StyledLoadingIndicator />
							) : upload.status === "uploading" ? (
								<ProgressCircle
									style={{
										background: `conic-gradient(${primaryColor} ${upload.progress * 360}deg, ${componentBackgroundColor} 0deg)`,
									}}
								>
									<InnerProgressCircle>{Math.floor(upload.progress * 100)}</InnerProgressCircle>
								</ProgressCircle>
							) : upload.status === "completed" ? (
								<Icon as={CheckmarkIcon} />
							) : (
								<Icon as={CloseIcon} />
							)}
							{upload.label}
						</Upload>
					))}
				</Container>
			)}
			{children}
		</VideoUploadContext.Provider>
	)
}

export default VideoUploadProvider

const Container = styled.div`
	position: fixed;
	right: 16px;
	top: 16px;
	width: 300px;
	pointer-events: auto;
	background-color: ${componentBackgroundColor};
	border: 1px solid ${componentBorderColor};
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const Upload = styled.div<{ $status: "preparing" | "uploading" | "failed" | "completed" }>`
	display: flex;
	align-items: center;
	gap: 8px;
	color: ${props =>
		props.$status === "completed" ? successColor : props.$status === "failed" ? dangerColor : "black"};
`

const StyledLoadingIndicator = styled(LoadingIndicator)`
	width: 24px;
	height: 24px;
`

const ProgressCircle = styled.div`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
`

const InnerProgressCircle = styled.div`
	position: absolute;
	width: ${24 - 6}px;
	height: ${24 - 6}px;
	border-radius: 50%;
	background-color: ${componentBackgroundColor};
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 8px;
	line-height: 1;
	font-weight: bold;
`

const Icon = styled.div`
	width: 24px;
	height: 24px;
`
